import type { BundleItem, Parameter, ParameterValue, RuleType, TemplateRule } from "./document";

/**
 * Rule summaries in the app's own vocabulary — `Include · Rope · Qty: people * 2`.
 *
 * The wording mirrors `RuleListDisplay` in the app so an author reading a rule on
 * a phone and in the editor sees the same sentence. It is display text only:
 * anything that decides behaviour lives in the shared engine.
 */

export function ruleTypeLabel(type: RuleType): string {
  switch (type) {
    case "include":
      return "Include";
    case "includeQuick":
      return "Quick item";
    case "exclude":
      return "Exclude";
    case "override":
      return "Override";
    case "conditional":
      return "Conditional";
    case "includeGroup":
      return "Include group";
    case "excludeGroup":
      return "Exclude group";
  }
}

function itemTitle(id: string | null | undefined, items: readonly BundleItem[]): string {
  if (!id) return "No item";
  const item = items.find((candidate) => candidate.id.toLowerCase() === id.toLowerCase());
  if (!item) return "Missing item";
  return item.title.trim() === "" ? "Untitled" : item.title;
}

export function normalizeTags(tags: readonly string[] | null | undefined): readonly string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const tag of tags ?? []) {
    const normalized = tag.trim().toLowerCase();
    if (normalized === "" || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

/** The items a group rule matches right now, by the engine's tag rules. */
export function groupMatches(rule: TemplateRule, items: readonly BundleItem[]): readonly BundleItem[] {
  if (rule.type !== "includeGroup" && rule.type !== "excludeGroup") return [];
  const tags = normalizeTags(rule.groupTags);
  if (tags.length === 0) return [];
  const matchAll = rule.groupMatchAll ?? false;
  return items.filter((item) => {
    const itemTags = normalizeTags(item.tags);
    return matchAll ? tags.every((tag) => itemTags.includes(tag)) : tags.some((tag) => itemTags.includes(tag));
  });
}

function valueText(value: ParameterValue | null | undefined): string {
  if (!value) return "?";
  return String(value.value);
}

function predicateSummary(rule: TemplateRule): string {
  const predicate = rule.predicate;
  if (!predicate || predicate.parameterKey.trim() === "") return "No condition";
  return `${predicate.parameterKey} ${predicate.op} ${valueText(predicate.value)}`;
}

/** The middle segment: what the rule is about. */
export function ruleDetail(rule: TemplateRule, items: readonly BundleItem[]): string {
  switch (rule.type) {
    case "includeGroup":
    case "excludeGroup": {
      const tags = normalizeTags(rule.groupTags);
      if (tags.length === 0) return "No tags";
      return `${rule.groupMatchAll ? "All of" : "Any of"}: ${tags.join(", ")}`;
    }
    case "conditional":
      return predicateSummary(rule);
    case "includeQuick": {
      const title = (rule.quickTitle ?? "").trim();
      return title === "" ? "Untitled" : title;
    }
    default:
      return itemTitle(rule.targetDefinitionID, items);
  }
}

/** `Include · Rope · Qty: people * 2` */
export function ruleSummary(rule: TemplateRule, items: readonly BundleItem[]): string {
  const parts = [ruleTypeLabel(rule.type), ruleDetail(rule, items)];
  const quantity = (rule.quantityExpression ?? "").trim();
  if (quantity !== "" && (rule.type === "include" || rule.type === "includeQuick")) {
    parts.push(`Qty: ${quantity}`);
  }
  if (rule.type === "conditional") {
    const nested = rule.conditionalRules?.length ?? 0;
    parts.push(`${nested} nested`);
  }
  return parts.join(" · ");
}

/** "Matches: Rope, Slings (2)" — a group rule's effect is otherwise invisible. */
export function ruleMatchesText(rule: TemplateRule, items: readonly BundleItem[]): string | null {
  if (rule.type !== "includeGroup" && rule.type !== "excludeGroup") return null;
  const matches = groupMatches(rule, items);
  if (matches.length === 0) return "Matches: none";
  const titles = matches.map((item) => (item.title.trim() === "" ? "Untitled" : item.title));
  const shown = titles.slice(0, 3).join(", ");
  return matches.length > 3
    ? `Matches: ${shown} +${matches.length - 3} more (${matches.length})`
    : `Matches: ${shown} (${matches.length})`;
}

/**
 * A rule's own problems, in the app's words. Deliberately a subset of
 * `RuleValidation`: the authoritative check runs in the shared engine when the
 * draft is validated, this is the inline hint while typing.
 */
export function ruleProblem(rule: TemplateRule, parameters: readonly Parameter[]): string | null {
  switch (rule.type) {
    case "includeGroup":
    case "excludeGroup":
      return normalizeTags(rule.groupTags).length === 0 ? "Select at least one tag." : null;
    case "conditional": {
      const key = rule.predicate?.parameterKey.trim() ?? "";
      if (key === "") return "Pick a parameter.";
      if (!parameters.some((parameter) => parameter.key.trim().toLowerCase() === key.toLowerCase())) {
        return "Unknown parameter key.";
      }
      return (rule.conditionalRules?.length ?? 0) === 0 ? "Add at least one nested rule." : null;
    }
    case "includeQuick":
      return (rule.quickTitle ?? "").trim() === "" ? "Enter a title." : null;
    case "override": {
      const hasOverride =
        (rule.overrideTitle ?? "").trim() !== "" ||
        (rule.overrideNotes ?? "").trim() !== "" ||
        rule.overrideWeight != null ||
        rule.overrideTags != null;
      if (!rule.targetDefinitionID) return "Select an item.";
      return hasOverride ? null : "Add at least one override.";
    }
    default:
      return rule.targetDefinitionID ? null : "Select an item.";
  }
}
